<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAttachmentsPanel',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'stage', 'unstage']);

  const props = defineProps({
    ...userComponentProps,
    parent_id: {
      type: String,
      required: false,
    },
    parent: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    layout: {
      type: String,
      required: false,
      default: 'list',
    },
    pending: {
      type: Array,
      required: false,
      default: [],
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    container: container.value,
    ux: ux.value,
  }));

  const $$load_container = () => {
    return props.parent_id || (props.parent && props.parent.id)
      ? $fn.value.fetch('list_attachment', {
          parameters: { 'owner-id': props.parent_id || props.parent.id },
          asResourceContainer: true,
        })
      : { data: [] };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'container' }, { name: 'ux' }],
    loaders: { container: $$load_container },
    ux: {},
    dataRefs: { container, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const ownerId = computed(() => {
    return props.parent_id || (props.parent && props.parent.id);
  });
  const items = computed(() => {
    if (loaded.value) {
      return container.value.data;
    }
    return null;
  });
  const isReadOnly = computed(() => {
    return props.readonly || (props.parent && props.parent.isReadOnly);
  });
  const hasAny = computed(() => {
    return (
      (items.value && items.value.length) ||
      (props.pending && props.pending.length)
    );
  });

  watch(
    () => props.parent_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent_id');
    }
  );
  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.layout,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('layout');
    }
  );
  watch(
    () => props.pending,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('pending');
    }
  );

  const act_5382094220310827_showModal_m3x0e6xfs_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
        attachment: _.cloneDeep($event.scope.item),
      },
    };
  };
  const act_2688566256707306_request_gxdnbxmdq_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_attachment',
      data: $event.scope.item,
    };
  };
  const act_2688566256707306_request_gxdnbxmdq_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_5962862420439144_showModal_hrk45ym1w_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
      },
    };
  };
  const act_96700093353179_showModal_fnzz9sg7o_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
      },
    };
  };
  const act_5974459770896956_emit_n1wtce3ep_getActionArgs = async ($event) => {
    return {
      event: 'stage',
      value: $event.data.modalResult.value,
    };
  };
  const inst_evh_5974459770896956_close_mllo9fez5_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.value;
  };
  const act_5974459770896956_showModal_73rjdh2ol_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        defer: true,
      },
    };
  };
  const act_3494165723496827_showModal_eo7zdntq8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
        attachment: _.cloneDeep($event.scope.item),
      },
    };
  };
  const act_4108270090928914_request_x9fgt9i0d_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_attachment',
      data: $event.scope.item,
    };
  };
  const act_4108270090928914_request_x9fgt9i0d_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_7119126147772776_emit_gpxh6y6n3_getActionArgs = async ($event) => {
    return {
      event: 'unstage',
      value: $event.scope.p._uid,
    };
  };

  const act_5382094220310827_reloadComponentData_t1zwpxqte =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5382094220310827_close_lvxhe1fwd =
    new actionTypes.EventHandlerImpl({
      action: act_5382094220310827_reloadComponentData_t1zwpxqte,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_5382094220310827_showModal_m3x0e6xfs =
    new actionTypes.ShowModalAction({
      actionArgs: act_5382094220310827_showModal_m3x0e6xfs_getActionArgs,
      events: [inst_evh_5382094220310827_close_lvxhe1fwd],
    });
  const inst_evh_5382094220310827_clickPreventStop_9yr0vmf6c =
    new actionTypes.EventHandlerImpl({
      action: act_5382094220310827_showModal_m3x0e6xfs,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5382094220310827_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5382094220310827_clickPreventStop_9yr0vmf6c],
      event: 'click.prevent.stop',
    });
  const act_2688566256707306_reloadComponentData_mzr9l573f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2688566256707306_success_zizqqbzbr =
    new actionTypes.EventHandlerImpl({
      action: act_2688566256707306_reloadComponentData_mzr9l573f,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_2688566256707306_request_gxdnbxmdq = new actionTypes.RequestAction({
    actionArgs: act_2688566256707306_request_gxdnbxmdq_getActionArgs,
    confirm: act_2688566256707306_request_gxdnbxmdq_getConfirm,
    events: [inst_evh_2688566256707306_success_zizqqbzbr],
  });
  const inst_evh_2688566256707306_clickPreventStop_o49f5toj3 =
    new actionTypes.EventHandlerImpl({
      action: act_2688566256707306_request_gxdnbxmdq,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_2688566256707306_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2688566256707306_clickPreventStop_o49f5toj3],
      event: 'click.prevent.stop',
    });
  const act_5962862420439144_reloadComponentData_u86fwjblm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5962862420439144_close_r046ckrq7 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_reloadComponentData_u86fwjblm,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_5962862420439144_showModal_hrk45ym1w =
    new actionTypes.ShowModalAction({
      actionArgs: act_5962862420439144_showModal_hrk45ym1w_getActionArgs,
      events: [inst_evh_5962862420439144_close_r046ckrq7],
    });
  const inst_evh_5962862420439144_clickPreventStop_cf2fp3pjx =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_showModal_hrk45ym1w,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5962862420439144_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_clickPreventStop_cf2fp3pjx],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_reloadComponentData_s89bnup04 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_96700093353179_close_fwrltcuwh =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_reloadComponentData_s89bnup04,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_96700093353179_showModal_fnzz9sg7o =
    new actionTypes.ShowModalAction({
      actionArgs: act_96700093353179_showModal_fnzz9sg7o_getActionArgs,
      events: [inst_evh_96700093353179_close_fwrltcuwh],
    });
  const inst_evh_96700093353179_clickPreventStop_y9ruf5e4c =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_showModal_fnzz9sg7o,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_y9ruf5e4c],
      event: 'click.prevent.stop',
    });
  const act_5974459770896956_emit_n1wtce3ep = new actionTypes.EmitAction({
    actionArgs: act_5974459770896956_emit_n1wtce3ep_getActionArgs,
  });
  const inst_evh_5974459770896956_close_mllo9fez5 =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_emit_n1wtce3ep,
      event: 'close',
      when: inst_evh_5974459770896956_close_mllo9fez5_getWhen,
      displayName: 'emit',
    });
  const act_5974459770896956_showModal_73rjdh2ol =
    new actionTypes.ShowModalAction({
      actionArgs: act_5974459770896956_showModal_73rjdh2ol_getActionArgs,
      events: [inst_evh_5974459770896956_close_mllo9fez5],
    });
  const inst_evh_5974459770896956_clickPreventStop_eudo2wd5y =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_showModal_73rjdh2ol,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5974459770896956_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5974459770896956_clickPreventStop_eudo2wd5y],
      event: 'click.prevent.stop',
    });
  const act_3494165723496827_reloadComponentData_dgsmjwdpk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_3494165723496827_close_ndzf0oaxo =
    new actionTypes.EventHandlerImpl({
      action: act_3494165723496827_reloadComponentData_dgsmjwdpk,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_3494165723496827_showModal_eo7zdntq8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3494165723496827_showModal_eo7zdntq8_getActionArgs,
      events: [inst_evh_3494165723496827_close_ndzf0oaxo],
    });
  const inst_evh_3494165723496827_clickPreventStop_lx4cedn7p =
    new actionTypes.EventHandlerImpl({
      action: act_3494165723496827_showModal_eo7zdntq8,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_3494165723496827_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3494165723496827_clickPreventStop_lx4cedn7p],
      event: 'click.prevent.stop',
    });
  const act_4108270090928914_reloadComponentData_xl49ju0hx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4108270090928914_success_qywy4vcvh =
    new actionTypes.EventHandlerImpl({
      action: act_4108270090928914_reloadComponentData_xl49ju0hx,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_4108270090928914_request_x9fgt9i0d = new actionTypes.RequestAction({
    actionArgs: act_4108270090928914_request_x9fgt9i0d_getActionArgs,
    confirm: act_4108270090928914_request_x9fgt9i0d_getConfirm,
    events: [inst_evh_4108270090928914_success_qywy4vcvh],
  });
  const inst_evh_4108270090928914_clickPreventStop_scott3ybe =
    new actionTypes.EventHandlerImpl({
      action: act_4108270090928914_request_x9fgt9i0d,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_4108270090928914_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4108270090928914_clickPreventStop_scott3ybe],
      event: 'click.prevent.stop',
    });
  const act_7119126147772776_emit_gpxh6y6n3 = new actionTypes.EmitAction({
    actionArgs: act_7119126147772776_emit_gpxh6y6n3_getActionArgs,
  });
  const inst_evh_7119126147772776_clickPreventStop_7g1fvq8lg =
    new actionTypes.EventHandlerImpl({
      action: act_7119126147772776_emit_gpxh6y6n3,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const inst_evh_7119126147772776_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7119126147772776_clickPreventStop_7g1fvq8lg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_5382094220310827_close_lvxhe1fwd,
    inst_evh_5382094220310827_clickPreventStop_9yr0vmf6c,
    inst_evh_5382094220310827_clickPreventStop,
    inst_evh_2688566256707306_success_zizqqbzbr,
    inst_evh_2688566256707306_clickPreventStop_o49f5toj3,
    inst_evh_2688566256707306_clickPreventStop,
    inst_evh_5962862420439144_close_r046ckrq7,
    inst_evh_5962862420439144_clickPreventStop_cf2fp3pjx,
    inst_evh_5962862420439144_clickPreventStop,
    inst_evh_96700093353179_close_fwrltcuwh,
    inst_evh_96700093353179_clickPreventStop_y9ruf5e4c,
    inst_evh_96700093353179_clickPreventStop,
    inst_evh_5974459770896956_close_mllo9fez5,
    inst_evh_5974459770896956_clickPreventStop_eudo2wd5y,
    inst_evh_5974459770896956_clickPreventStop,
    inst_evh_3494165723496827_close_ndzf0oaxo,
    inst_evh_3494165723496827_clickPreventStop_lx4cedn7p,
    inst_evh_3494165723496827_clickPreventStop,
    inst_evh_4108270090928914_success_qywy4vcvh,
    inst_evh_4108270090928914_clickPreventStop_scott3ybe,
    inst_evh_4108270090928914_clickPreventStop,
    inst_evh_7119126147772776_clickPreventStop_7g1fvq8lg,
    inst_evh_7119126147772776_clickPreventStop,
  };

  const evh_5382094220310827_close_lvxhe1fwd = async (event, scope) => {
    return await actions.inst_evh_5382094220310827_close_lvxhe1fwd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5382094220310827_clickPreventStop_9yr0vmf6c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5382094220310827_clickPreventStop_9yr0vmf6c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5382094220310827_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5382094220310827_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_success_zizqqbzbr = async (event, scope) => {
    return await actions.inst_evh_2688566256707306_success_zizqqbzbr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_clickPreventStop_o49f5toj3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2688566256707306_clickPreventStop_o49f5toj3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2688566256707306_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_close_r046ckrq7 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_close_r046ckrq7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop_cf2fp3pjx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop_cf2fp3pjx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_close_fwrltcuwh = async (event, scope) => {
    return await actions.inst_evh_96700093353179_close_fwrltcuwh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_y9ruf5e4c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_y9ruf5e4c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_close_mllo9fez5 = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_close_mllo9fez5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPreventStop_eudo2wd5y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5974459770896956_clickPreventStop_eudo2wd5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_close_ndzf0oaxo = async (event, scope) => {
    return await actions.inst_evh_3494165723496827_close_ndzf0oaxo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_clickPreventStop_lx4cedn7p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3494165723496827_clickPreventStop_lx4cedn7p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3494165723496827_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_success_qywy4vcvh = async (event, scope) => {
    return await actions.inst_evh_4108270090928914_success_qywy4vcvh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_clickPreventStop_scott3ybe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4108270090928914_clickPreventStop_scott3ybe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4108270090928914_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7119126147772776_clickPreventStop_7g1fvq8lg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7119126147772776_clickPreventStop_7g1fvq8lg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7119126147772776_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7119126147772776_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" v-if="layout !== 'wrap'">
    <ItpBox direction="column" v-if="items && items.length">
      <ItpBox
        :key="item.id"
        alignItems="center"
        class="border rounded rounded-3 p-2 mb-2 overflow-hidden"
        v-for="item in items"
      >
        <UcFileIcon size="small" :filename="item.file_name" class="me-2">
        </UcFileIcon>
        <ItpLink
          :text="item.file_name"
          :href="item.download_url"
          :tooltip="item.file_name"
          :downloadFileName="item.file_name"
          download-link-only
          use-auth
        >
        </ItpLink>
        <BDropdown
          boundary="viewport"
          size="sm"
          toggle-class="btn-default-borderless"
          no-caret
          variant="none"
          dropleft
          class="ms-auto mb-auto"
          v-if="!item.locked"
        >
          <template #button-content>
            <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
          </template>
          <BDropdownItem
            @click.prevent.stop="
              evh_5382094220310827_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
          </BDropdownItem>
          <BDropdownItem
            @click.prevent.stop="
              evh_2688566256707306_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
          </BDropdownItem>
        </BDropdown>
      </ItpBox>
    </ItpBox>
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="ms-auto"
      v-if="!isReadOnly"
      @click.prevent.stop="evh_5962862420439144_clickPreventStop($event, {})"
    >
    </ItpLink>
  </ItpBox>
  <div v-if="layout === 'wrap'">
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="itp-attachment-panel__upload d-inline-block"
      v-if="!isReadOnly && ownerId"
      @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})"
    >
    </ItpLink>
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="itp-attachment-panel__upload d-inline-block"
      v-if="!isReadOnly && !ownerId"
      @click.prevent.stop="evh_5974459770896956_clickPreventStop($event, {})"
    >
    </ItpLink>
    <div class="itp-attachment-grid mt-2" v-if="hasAny">
      <div
        :key="item.id"
        class="itp-attachment-box border rounded-2 px-2 py-1 d-flex align-items-center gap-2"
        v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
      >
        <UcFileIcon
          size="small"
          :filename="item.file_name"
          class="flex-shrink-0"
        >
        </UcFileIcon>
        <ItpLink
          :text="item.file_name"
          :href="item.download_url"
          :tooltip="item.file_name"
          :downloadFileName="item.file_name"
          download-link-only
          use-auth
          class="itp-attachment-box__name"
        >
        </ItpLink>
        <ItpText class="text-muted small text-nowrap flex-shrink-0">
          {{ filesize(item.file_size) }}
        </ItpText>
        <BDropdown
          boundary="viewport"
          size="sm"
          toggle-class="btn-default-borderless"
          no-caret
          variant="none"
          dropleft
          class="flex-shrink-0"
          v-if="!item.locked && !isReadOnly"
        >
          <template #button-content>
            <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
          </template>
          <BDropdownItem
            @click.prevent.stop="
              evh_3494165723496827_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
          </BDropdownItem>
          <BDropdownItem
            @click.prevent.stop="
              evh_4108270090928914_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
          </BDropdownItem>
        </BDropdown>
      </div>
      <div
        :key="p._uid"
        class="itp-attachment-box itp-attachment-box--pending border rounded-2 px-2 py-1 d-flex align-items-center gap-2 bg-body-secondary"
        v-for="p in pending"
      >
        <UcFileIcon size="small" :filename="p.file.name" class="flex-shrink-0">
        </UcFileIcon>
        <span class="itp-attachment-box__name">
          {{ p.file.name }}
        </span>
        <ItpText class="text-muted small text-nowrap flex-shrink-0">
          {{ filesize(p.file.size) }}
        </ItpText>
        <ItpButton
          size="sm"
          variant="light"
          icon="fa-times"
          :tooltip="pgettext('Command', 'Remove')"
          class="flex-shrink-0"
          v-if="!isReadOnly"
          @click.prevent.stop="
            evh_7119126147772776_clickPreventStop($event, { p })
          "
        >
        </ItpButton>
      </div>
    </div>
  </div>
</template>
